import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import MembershipForm from '../home/components/MembershipForm';
import Footer from '../home/components/Footer';

const QR_CODE_URL =
  'https://storage.readdy-site.link/project_files/6f264cdb-e651-41f3-9b8c-2dc509d942b2/858e8da7-2784-4f1d-ac2f-573e9dfe9b16_L_gainfriends_2dbarcodes_BW.png?v=7cb39c0ee81480bf9e8aef99c485c7a8';

const applicationSteps = [
  {
    step: '01',
    icon: 'ri-search-eye-line',
    title: '了解社團',
    desc: '瀏覽網站或加入 Facebook 社群，了解 YMC Taipei 的宗旨、活動與成員組成，確認與您的發展目標相符。',
    color: 'orange',
  },
  {
    step: '02',
    icon: 'ri-user-add-line',
    title: '填寫申請表',
    desc: '完整填寫下方入社申請表，包含個人資料、公司資訊與入會動機，若有會員推薦人請一併填寫。',
    color: 'orange',
  },
  {
    step: '03',
    icon: 'ri-file-check-line',
    title: '理事會審核',
    desc: '申請資料送出後，將由理事會進行審核。審核期間約 7–14 個工作天，我們會以電話或電郵通知結果。',
    color: 'orange',
  },
  {
    step: '04',
    icon: 'ri-handshake-line',
    title: '繳費與入社',
    desc: '審核通過後，依通知繳交會費，即正式成為 YMC Taipei 會員，可參與所有例會與專屬活動。',
    color: 'orange',
  },
];

interface FaqItem {
  q: string;
  a: string;
  cta?: { label: string; href: string; icon: string };
}

const faqs: FaqItem[] = [
  {
    q: 'YMC Taipei 的宗旨是什麼？',
    a: '以「研究、友誼、服務」為三大核心宗旨，旨在研究工商企業各項課題、增進青年企業家之友誼與合作、培養健全人格並服務社會人群。自 1962 年創立至今，超過 60 年來持續為台灣青年企業家提供最優質的交流平台。',
  },
  {
    q: '如何加入 YMC Taipei？',
    a: '只要透過 Line 官方帳號聯繫我們，即可開始入社諮詢！我們會親自為您說明資格條件、審核流程與社費資訊，歡迎對企業研究與工商交流有熱忱的專業人士，不論是企業主、高階經理人或工商業背景專業人士皆可申請。',
    cta: {
      label: '透過 Line 立即諮詢加入',
      href: 'https://lin.ee/pVaOePX',
      icon: 'ri-chat-smile-2-line',
    },
  },
  {
    q: '申請入社一定要是企業主嗎？',
    a: '成員多為中小企業主、高階經理人或具備工商業背景的專業人士，但並非只限企業主。申請時填寫入社申請表，通過理事會審核後即可入社。若有社友推薦人可一併填寫，但非必要條件。',
  },
  {
    q: '月例會內容包含哪些？',
    a: '每月定期舉辦主題講座，邀請業界專家主講，議題涵蓋企業韌性、AI 趨勢、品牌建立、東協商機、新創發展等前瞻議題，每場由輪值理事主持規劃。例會同時也是社友聯誼交流的重要場合，讓大家在輕鬆愉快的氛圍中深化情誼。',
  },
  {
    q: 'YMC Taipei 與一般商業社團有何不同？',
    a: 'YMC Taipei 強調「研究」與「提升經營素質」，不僅注重社友網絡的連結與企業經驗傳承，更透過全台青企聯盟與台中、台南、高雄、宜蘭、屏東等各地分社保持緊密連結，讓每位會員都能建立跨城市、跨產業的商業關係，並透過年度全國活動與海外考察拓展國際視野。',
  },
  {
    q: '社費是多少？',
    a: '整年度社費為 15,000 元。',
  },
  {
    q: '如何了解青企全台各分社訊息？',
    a: '可透過各分社的 Facebook 社群取得最新資訊。全台分社遍佈台北市、宜蘭、台中市、台南市、高雄市、高雄首都及屏東縣等地，歡迎直接前往各社 Facebook 頁面，或透過本頁聯絡 YMC Taipei 取得更多資訊。',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.a,
    },
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首頁', item: 'https://ymctaipei.com/' },
    { '@type': 'ListItem', position: 2, name: '加入 / 聯絡我們', item: 'https://ymctaipei.com/contact' },
  ],
};

const contactPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: '加入 / 聯絡我們 | 台北市青年企業研究社 YMC Taipei',
  description: '了解如何加入 YMC Taipei 青企社，查閱入社流程、社費說明與常見問題，立即填寫申請表開啟青企旅程。',
  url: 'https://ymctaipei.com/contact',
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: ['h1', 'h2', '.faq-answer'],
  },
};

export default function ContactPage() {
  const [showQR, setShowQR] = useState(false);
  return (
    <div className="min-h-screen bg-white">
      {/* React 19 document metadata hoisting */}
      <title>加入青企社 | 台北市青年企業研究社 YMC Taipei</title>
      <meta name="description" content="了解如何加入 YMC Taipei 台北青企社，包含入社流程、審核機制、社費說明與常見問題，立即填寫申請表，成為工商青年企業家大家庭的一員。" />
      <meta name="keywords" content="加入青企社,YMC Taipei入社,台北青年企業家,入社申請,青企社費" />
      <link rel="canonical" href="https://ymctaipei.com/contact" />
      <meta property="og:title" content="加入 / 聯絡 YMC Taipei 台北青企社" />
      <meta property="og:description" content="了解如何加入 YMC Taipei，入社流程、社費與常見問題一次解答。" />
      <meta property="og:url" content="https://ymctaipei.com/contact" />
      <meta property="og:type" content="website" />

      {/* AEO Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(contactPageSchema) }}
      />

      <Navbar />

      {/* Page Hero */}
      <section className="relative h-[280px] sm:h-[380px] md:h-[540px] overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/d3dedaf6b355d8e073ca0c9829fdba1c.jpg"
          alt="加入 YMC Taipei"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/65"></div>
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4 sm:px-6 pt-20">
          <p className="text-xs sm:text-sm font-light text-orange-300 tracking-widest uppercase mb-3">Join &amp; Contact</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-5 drop-shadow-lg">
            加入 / 聯絡我們
          </h1>
          <p className="text-base sm:text-lg text-white/85 max-w-2xl mx-auto leading-relaxed mb-8">
            歡迎加入 YMC Taipei，與超過 60 年歷史的青年工商社團一同成長、交流、服務。
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://lin.ee/pVaOePX"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-bold transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <span className="flex items-center gap-2 justify-center">
                <i className="ri-chat-smile-2-line w-4 h-4 flex items-center justify-center"></i>
                立即申請入社
              </span>
            </a>
            <a
              href="https://www.facebook.com/groups/138690069542554/user/100066890010443"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="w-full sm:w-auto px-6 py-3 bg-white/15 hover:bg-white/25 border border-white/40 text-white rounded-lg font-bold transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <span className="flex items-center gap-2 justify-center">
                <i className="ri-facebook-fill w-4 h-4 flex items-center justify-center"></i>
                加入 Facebook 社群
              </span>
            </a>
          </div>
        </div>
      </section>



      {/* Application Process */}
      <section id="process" className="py-16 sm:py-24 bg-stone-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-5">
              <i className="ri-road-map-line text-orange-700 w-4 h-4 flex items-center justify-center"></i>
              <span className="text-sm font-semibold text-orange-700">Application Process</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4">入社申請流程</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-xl mx-auto">
              四個簡單步驟，即可正式加入 YMC Taipei 大家庭
            </p>
          </div>

          {/* Steps */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {applicationSteps.map((s, i) => (
              <div key={i} className="relative bg-white rounded-2xl p-6 flex flex-col items-center text-center group hover:-translate-y-1 transition-transform duration-300 cursor-default">
                {/* connector line */}
                {i < applicationSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 right-0 translate-x-1/2 w-6 h-0.5 bg-orange-200 z-10"></div>
                )}
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-orange-100 mb-4">
                  <i className={`${s.icon} text-2xl text-orange-600 w-7 h-7 flex items-center justify-center`}></i>
                </div>
                <span className="text-xs font-black text-orange-400 tracking-widest mb-1">STEP {s.step}</span>
                <h3 className="text-lg font-black text-gray-900 mb-3">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Line CTA — below steps */}
          <div className="mt-12 sm:mt-14 bg-white rounded-2xl px-6 sm:px-10 py-8 sm:py-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
            {/* Left text */}
            <div className="flex-1 text-center sm:text-left">
              <p className="text-xs font-semibold text-orange-500 tracking-widest uppercase mb-2">Ready to join?</p>
              <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">
                準備好加入了嗎？
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed max-w-md">
                透過 LINE 官方帳號與我們聯繫，由專人為您說明入社細節，讓您的青企之旅從這裡正式開始！
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-shrink-0 w-full sm:w-auto">
              {/* Direct Link */}
              <a
                href="https://lin.ee/pVaOePX"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-6 py-3 bg-emerald-700 hover:bg-emerald-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-chat-smile-2-line w-4 h-4 flex items-center justify-center"></i>
                LINE 立即諮詢
              </a>

              {/* QR Code Button */}
              <div className="relative w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setShowQR((v) => !v)}
                  className="w-full sm:w-auto px-6 py-3 bg-white border border-emerald-200 text-emerald-800 rounded-xl font-semibold flex items-center justify-center gap-2 hover:border-emerald-400 hover:bg-emerald-50 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-qr-code-line w-4 h-4 flex items-center justify-center"></i>
                  掃碼加入
                </button>

                {/* QR Popover */}
                {showQR && (
                  <>
                    {/* Backdrop */}
                    <div className="fixed inset-0 z-40" onClick={() => setShowQR(false)} />
                    {/* Popup card */}
                    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-3 w-56 bg-white rounded-2xl border border-emerald-100 p-4 flex flex-col items-center gap-3 animate-fade-in-up">
                      {/* Arrow */}
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-r border-b border-emerald-100 rotate-45"></div>
                      <p className="text-xs font-semibold text-emerald-700 tracking-wide text-center">
                        LINE 官方帳號
                      </p>
                      <div className="w-36 h-36 flex items-center justify-center rounded-xl overflow-hidden bg-white border border-stone-100 p-2">
                        <img
                          src={QR_CODE_URL}
                          alt="掃描 QR Code 加入 YMC Taipei Line 官方帳號"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <p className="text-[11px] text-gray-400 text-center leading-relaxed">
                        用手機掃描即可加入<br />YMC Taipei Line 官方帳號
                      </p>
                      <a
                        href="https://lin.ee/pVaOePX"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-xs font-bold text-center transition-colors duration-200 whitespace-nowrap cursor-pointer"
                        onClick={() => setShowQR(false)}
                      >
                        直接點擊加入
                      </a>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Facebook Community Banner */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="relative overflow-hidden rounded-2xl bg-[#1877F2] flex flex-col md:flex-row items-center gap-8 px-8 sm:px-12 py-10 sm:py-14">
            {/* BG decoration */}
            <div className="absolute right-0 top-0 w-64 h-64 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>
            <div className="absolute left-10 bottom-0 w-40 h-40 rounded-full bg-white/5 translate-y-1/3 pointer-events-none"></div>

            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white/20 flex-shrink-0">
              <i className="ri-facebook-fill text-white text-4xl w-10 h-10 flex items-center justify-center"></i>
            </div>

            <div className="flex-1 text-center md:text-left">
              <h3 className="text-2xl sm:text-3xl font-black text-white mb-2">加入 YMC Taipei Facebook 社群</h3>
              <p className="text-white/80 text-base leading-relaxed max-w-xl">
                追蹤活動公告、照片回顧與會員交流，第一手掌握社團最新動態。歡迎先加入社群認識我們，再提交入社申請！
              </p>
            </div>

            <a
              href="https://www.facebook.com/groups/138690069542554/user/100066890010443"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex-shrink-0 px-8 py-4 bg-white text-[#1877F2] rounded-xl font-black text-base hover:bg-orange-50 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-external-link-line w-5 h-5 flex items-center justify-center"></i>
                前往社群
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 sm:py-20 bg-stone-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-stone-200 rounded-full mb-5">
              <i className="ri-question-answer-line text-stone-700 w-4 h-4 flex items-center justify-center"></i>
              <span className="text-sm font-semibold text-stone-700">FAQ</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">常見問題</h2>
            <p className="text-base text-gray-500">入社前最常問到的問題，快速為您解答</p>
          </div>
          <div className="space-y-4" itemScope itemType="https://schema.org/FAQPage">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl p-6" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-100 flex-shrink-0 mt-0.5">
                    <span className="text-sm font-black text-orange-600">Q</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 text-base mb-2" itemProp="name">{faq.q}</h4>
                    <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                      <p className="text-gray-600 text-sm leading-relaxed faq-answer" itemProp="text">{faq.a}</p>
                    </div>
                    {faq.cta && (
                      <a
                        href={faq.cta.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
                      >
                        <i className={`${faq.cta.icon} w-4 h-4 flex items-center justify-center`}></i>
                        {faq.cta.label}
                        <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Form */}
      <div id="apply">
        <MembershipForm />
      </div>

      <Footer />
    </div>
  );
}
